var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState } from 'react';
import { Pane } from '@ui/molecules/Pane';
import { InlineNotification } from '@ui/molecules/InlineNotification/InlineNotification';
import { Toggle } from '@ui/molecules/inputs/Toggle/Toggle';
import { Button, Tag, Text } from '@ui';
import { translate } from '@utils/i18n';
import apiClient from '@utils/api-client';
import useGetPushNotificationSettings from '@hooks/requests/push-notification-settings.get.hook';
import { desiredFor, deviatesFromDefault, draftFromRows, hasChanges, toPayload } from './types';
import './PushNotificationTypesPanelStyles.scss';
const NAMESPACE = 'features.settings.push_notification_settings.types_panel';
const t = (key) => translate(key, { namespace: NAMESPACE });
/**
 * The hidden per-tenant switchboard for push notifications — InspectPoint staff
 * only, here and on the endpoints it uses.
 *
 * A React island in the ERB settings page, modelled on the technician
 * push-device panel. Two levels: the master switch (the same Tenant field
 * ActiveAdmin edits) and one toggle per entry in the Rails-side catalog
 * (PushNotifications::Catalog) — adding a type in code adds a row here with no
 * change to this component. Save sends the FULL desired state; Rails stores only
 * what differs from the catalog default.
 */
export function PushNotificationTypesPanel({ settingsUrl }) {
    const { data, loading, error, refresh } = useGetPushNotificationSettings(settingsUrl);
    const [draft, setDraft] = useState({});
    const [masterDraft, setMasterDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState(null);
    // Every read from Rails resets the drafts to what is actually stored, so the
    // page never shows an edit that did not land.
    useEffect(() => {
        if (data) {
            setDraft(draftFromRows(data.types));
            setMasterDraft(data.masterEnabled);
        }
    }, [data]);
    if (!data) {
        return (React.createElement(Pane, { title: t('title'), subtitle: t('subtitle') },
            React.createElement("div", { className: "push-notification-types__placeholder" },
                loading && React.createElement("i", { className: "fa-light fa-circle-notch fa-spin", "aria-hidden": "true" }),
                React.createElement(Text, { variant: "body" }, t(!loading && error ? 'load_failed' : 'loading')))));
    }
    const master = masterDraft !== null && masterDraft !== void 0 ? masterDraft : data.masterEnabled;
    const dirty = hasChanges(data.types, draft) || master !== data.masterEnabled;
    const save = () => __awaiter(this, void 0, void 0, function* () {
        setSaving(true);
        setResult(null);
        try {
            yield apiClient.patch(data.urls.update, toPayload(data.types, draft, master));
            setResult({ ok: true, message: t('saved') });
            // Re-read rather than trusting the local draft: Rails decides what is an
            // override and what is merely the default.
            yield refresh();
        }
        catch (err) {
            setResult({ ok: false, message: t('save_failed') });
        }
        finally {
            setSaving(false);
        }
    });
    const discard = () => {
        setDraft(draftFromRows(data.types));
        setMasterDraft(data.masterEnabled);
        setResult(null);
    };
    const setType = (type, enabled) => {
        setResult(null);
        setDraft((previous) => (Object.assign(Object.assign({}, previous), { [type]: enabled })));
    };
    // Off reaches further than any type switch — it also stops device
    // registration, the in-app notifications toggle and the back-office device
    // section — so it should never happen on a stray click.
    const setMaster = (enabled) => {
        // eslint-disable-next-line no-alert
        if (!enabled && !window.confirm(t('master_off_confirm')))
            return;
        setResult(null);
        setMasterDraft(enabled);
    };
    return (React.createElement(Pane, { title: t('title'), subtitle: t('subtitle'), footer: React.createElement("div", { className: "push-notification-types__footer" },
            React.createElement("div", { className: "push-notification-types__buttons" },
                React.createElement(Button, { variant: "primary", disabled: !dirty || saving, onClick: () => {
                        void save();
                    } }, saving ? t('saving') : t('save')),
                React.createElement(Button, { variant: "secondary", disabled: !dirty || saving, onClick: discard }, t('discard'))),
            result && (React.createElement(Text, { variant: "x-small", role: "alert" }, result.message))) },
        !data.tierConfigured && (React.createElement(InlineNotification, { variant: "neutral", role: "status", className: "push-notification-types__notice" }, t('tier_not_configured'))),
        React.createElement("div", { className: "push-notification-types__master" },
            React.createElement(Toggle, { checked: master, disabled: saving, label: t('master_label'), helperText: t('master_helper'), onChange: (event) => setMaster(event.target.checked) }),
            React.createElement("div", { className: "push-notification-types__meta" },
                React.createElement(Tag, { label: t('master_tag'), variant: "purple", readOnly: true }))),
        !master && (React.createElement(InlineNotification, { variant: "warning", role: "status", className: "push-notification-types__notice" }, t('master_off'))),
        React.createElement("ul", { className: "push-notification-types__list" }, data.types.map((row) => (React.createElement(TypeRow, { key: row.type, row: row, checked: desiredFor(row, draft), deviates: deviatesFromDefault(row, draft), disabled: saving, onChange: (enabled) => setType(row.type, enabled) }))))));
}
function TypeRow({ row, checked, deviates, disabled, onChange }) {
    return (React.createElement("li", { className: "push-notification-types__row" },
        React.createElement(Toggle, { checked: checked, disabled: disabled, label: row.label, helperText: row.description, onChange: (event) => onChange(event.target.checked) }),
        React.createElement("div", { className: "push-notification-types__meta" },
            React.createElement(Tag, { label: t(row.defaultEnabled ? 'default_on' : 'default_off'), variant: "gray", readOnly: true }),
            deviates && React.createElement(Tag, { label: t('overridden'), variant: "blue", readOnly: true }))));
}
export default PushNotificationTypesPanel;
